import { useEffect, useRef, useState } from "react";
import { Send, ShieldAlert, Clock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { KidAvatar } from "@/components/live/KidAvatar";
import { VoiceInput } from "@/components/buddy/VoiceInput";

const Bubble = ({ m }) => (
  <div className={`flex gap-2 items-end ${m.me ? "flex-row-reverse" : ""}`} data-testid={`school-msg-${m.id}`}>
    <KidAvatar a={m.avatar} className="w-9 h-9" />
    <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${m.blocked ? "bg-red-50 text-red-700 border border-red-200" : m.me ? "bg-[#2C4A3E] text-white" : "bg-white"}`}>
      <b className="block text-xs opacity-70">{m.me ? "You" : m.name} · {new Date(m.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</b>
      {m.blocked ? <span><ShieldAlert className="inline w-3.5 h-3.5" /> Not sent. {m.reason}</span> : m.text}
    </div>
  </div>
);

export default function SchoolChat() {
  const [d, setD] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const after = useRef("");
  const end = useRef(null);
  const nav = useNavigate();
  const add = (list) => setMsgs((m) => [...m, ...list.filter((x) => !m.some((y) => y.id === x.id))]);
  useEffect(() => {
    let alive = true, t;
    const poll = async () => {
      try { const r = await http.get("/school-chat/feed", { params: { after: after.current } }); if (!alive) return; setD(r.data);
        if (r.data.messages.length) { after.current = r.data.messages[r.data.messages.length - 1].created_at; add(r.data.messages); } }
      catch (e) { if (e.response?.status === 403) { toast.error(formatApiError(e)); nav("/", { replace: true }); return; } }
      if (alive) t = setTimeout(poll, 4000);
    };
    poll();
    return () => { alive = false; clearTimeout(t); };
  }, [nav]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [msgs.length]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { const r = await http.post("/school-chat/messages", { text }); add([r.data]); if (r.data.blocked) toast.error(r.data.reason); setText(""); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (!d) return <div className="max-w-5xl mx-auto px-6 py-20 text-stone-500">Loading…</div>;
  const canChat = d.open && d.consent;
  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 pb-24" data-testid="school-chat-page">
      <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C87D53]">School-Day Chat · {d.grade_label}</span>
      <h1 className="font-display text-4xl sm:text-5xl text-[#2C4A3E] mt-3">Talk about school, kindly</h1>
      <div className="flex flex-wrap gap-3 mt-4 text-sm">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ${d.open ? "bg-[#C8DCCB] text-[#2C4A3E]" : "bg-[#F1EADB] text-stone-600"}`} data-testid="school-chat-status" data-open={d.open}><Clock className="w-4 h-4" /> {d.open ? "Open now" : "Closed now"} · {d.hours}</span>
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E6DEC3] text-stone-600"><ShieldCheck className="w-4 h-4" /> Every message is checked for safety and shared with your grown-ups</span>
      </div>
      <section className="mt-8 rounded-3xl bg-[#F7F3EB] border border-[#E6DEC3] flex flex-col h-[560px]" data-testid="school-chat-box">
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {msgs.map((m) => <Bubble key={m.id} m={m} />)}
          {!msgs.length && <p className="text-sm text-stone-500" data-testid="school-chat-empty">No messages yet today. Say hi to your grade and share what you're learning!</p>}
          <div ref={end} />
        </div>
        {canChat ? (
          <form onSubmit={send} className="p-3 border-t border-[#E6DEC3] flex gap-2">
            <VoiceInput onText={(t) => setText((x) => (x ? `${x} ${t}` : t))} />
            <input value={text} onChange={(e) => setText(e.target.value)} maxLength={300} placeholder="What are you learning today?" data-testid="school-chat-input" className="flex-1 min-w-0 px-4 py-2 rounded-full bg-white border border-[#E6DEC3] text-sm focus:outline-none focus:border-[#2C4A3E]" />
            <button data-testid="school-chat-send" className="w-10 h-10 rounded-full bg-[#C87D53] text-white grid place-items-center"><Send className="w-4 h-4" /></button>
          </form>
        ) : <p className="p-4 text-sm text-stone-500 border-t border-[#E6DEC3]" data-testid="school-chat-locked">{!d.consent ? "A parent or teacher needs to approve chat first." : `Chat opens during school hours: ${d.hours}.`}</p>}
      </section>
    </div>
  );
}
